/**
 * B2 · সার্চ, খালি — `/search`
 * B3 · সার্চ ফলাফল — `/search?q=`
 * ১১ · সেবাটি আসছে — the same route when the server answers `route: "domain_not_live"`.
 *
 * Frames: `Oprappo P0 Screens.dc.html` "B2 · সার্চ, খালি" and "B3 · সার্চ ফলাফল";
 * `Oprappo P0 States.dc.html` "১১ · সেবাটি আসছে".
 *
 * SEARCH IS ONE DOOR (audit finding 01). The empty query renders B2. A non-empty query
 * is sent to `POST /v1/search` and the SERVER says what it was: `lookup`, `ai`, or
 * `domain_not_live`. This component switches on `result.route` and nowhere on the query
 * text — see `./client.ts`, which imports no heuristic, and `./use-search.ts`, which
 * sends the string and decides nothing about it.
 *
 * ── WHAT IS WIRED HERE ──────────────────────────────────────────────────────
 * The call is real. `useSearch` posts through the shared transport, and the answer
 * decides which of four screens renders: the results surface, the hand-off to the
 * conversation (or, for somebody who has not signed in, the door to one), or the eleventh
 * state — whose every sentence, alternative and footer is the server's own, already
 * localised.
 *
 * B3's CARDS ARE WIRED TOO. They used to be two constants in `./data.ts` — `রহিম ইলেকট্রিক`
 * with a rating and an ETA — rendered for every query. They are `result.groups`, rendered
 * field by field, and a field the door does not send is a line that does not appear.
 *
 * WHAT THAT COST, AND WHAT IT COSTS NOW. For a while it cost the whole screen: the door's
 * full-text rung returned `{ offering_id, title_bn, type, rank }`, its group labels were
 * empty, and the rung was never reached for B3's own query anyway — so this screen rendered
 * its chrome above an EMPTY column, and the 2% fidelity gate passed that at 1.98%, because
 * a blank results area on a 360×680 frame is under two percent of its pixels.
 *
 * ── W2-08: EVERYTHING ON IT DOES WHAT IT SAYS ───────────────────────────────
 *   · a card is a button to `/offering/<slug>` — the path the door composed — and a card
 *     for a listing with no page is not a button at all;
 *   · a chip is a question: tapping it asks the Worker again under that filter, and the
 *     list is the answer, not the old list under a new highlight;
 *   · recents are the person's own (the Worker's for a signed-in person, this device's for
 *     a signed-out one) and the popular chips are the Worker's;
 *   · the pinned AI card is the input's label, so tapping it puts the cursor in the field;
 *   · the eleventh state's `চালু হলে জানান` records a request and its rows open listings;
 *   · an empty column says where to look instead, in the Worker's words.
 * And two things are GONE: B3's footer strip, which promised free resources and courses
 * "below" on every answer, and A6's location sheet on `কাছাকাছি`, which asked for a location
 * nothing could use — the Worker measures "near" from the person's AREA, and has no way to
 * turn a phone's position into one. `docs/deviations/W2-08.md` records both.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ── WHY THE PIECES ARE HOOK-FREE ────────────────────────────────────────────
 * `SearchResults`, `ResultCard`, `NotLiveAlternatives` and `SearchSignIn` take everything
 * they act on as props — the answer, the lit chip, and `SearchActions`. That is what lets
 * `__tests__/interaction.test.tsx` call them, find the element a person would tap, invoke
 * its handler and assert what followed, in this suite's DOM-free `node` environment.
 * `SearchScreen` is the only place the router and the controller are reached for.
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams, type NavigateFunction } from 'react-router-dom';
import { Chip, Icon, IconButton } from '@oprappo/ui';
import {
  formatTaka,
  type SearchBrowse,
  type SearchFilter,
  type SearchFilterState,
  type SearchSignIn as SearchSignInCopy,
} from '@oprappo/contracts';
import { isSignedOut } from '../../api/client.js';
import { FIRST_RUN_SIGN_IN_PATH, signInHref } from '../../auth/SignInPrompt.js';
import { isFirstRun } from '../../auth/session.js';
import { Frame, SAND_FRAME } from '../onboarding/frame.js';
import { Glyph } from '../onboarding/glyphs.js';
import { SurfaceStates } from '../../states/boundary.js';
import { LoadingState } from '../../states/LoadingState.js';
import { OfflineState } from '../../states/OfflineState.js';
import { B2_COPY, DOMAIN_NOT_LIVE_COPY, RESULT_FILTERS } from './data.js';
import {
  loadPopular,
  loadRecents,
  postNotify,
  type DomainNotLiveAlternative,
  type DomainNotLiveDetail,
  type SearchResult,
  type SearchResultItem,
} from './client.js';
import { useSearch, type SearchController } from './use-search.js';

/* ------------------------------------------------------------ actions --- */

/** Everything a person can do on the results surface, as the screen hands it down. */
export interface SearchActions {
  /** A chip was tapped. */
  filter: (id: SearchFilter) => void;
  /** Open an app path the Worker composed — a card, a browse row, an alternative. */
  open: (to: string) => void;
  back: () => void;
}

/** The one place the controller and the router meet. The tests build theirs with it too. */
export function searchActions(controller: SearchController, navigate: NavigateFunction): SearchActions {
  return {
    filter: (id) => controller.choose(id),
    open: (to) => navigate(to),
    back: () => navigate(-1),
  };
}

/**
 * Where the sign-in door sends somebody who was at `/search?q=…`, carrying the query.
 *
 * `auth/SignInPrompt.tsx § signInHref` is the door's own rule: A7 with `?next=` for a
 * person who has signed in on this phone before, A1 for a phone nobody has. A1's link
 * carries `next` too, so that the query survives whichever door it goes through for as
 * far as the flow carries it (REPORTED in `docs/deviations/W2-08.md`: the sign-in screens
 * do not yet read `next`).
 */
export function searchSignInHref(q: string, firstRun: boolean): string {
  const returnTo = `/search?q=${encodeURIComponent(q)}`;
  return firstRun ? `${FIRST_RUN_SIGN_IN_PATH}?next=${encodeURIComponent(returnTo)}` : signInHref(returnTo, false);
}

/* ------------------------------------------------------------- screen --- */

export function SearchScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get('q') ?? '').trim();
  // Called unconditionally, above every branch: an empty query resolves to `idle` and
  // sends nothing, rather than the hook being skipped on one render and not the next.
  const { controller, filter, state } = useSearch(q);

  if (q === '') {
    return <SearchEmpty onSubmit={(next) => setParams({ q: next })} />;
  }

  // S3, on the screen it is drawn for, with the query still in the field — "nothing is
  // lost if it fails" is the frame note, and it is only true if the field keeps it. A chip
  // asked again keeps the answer before it on screen instead (`previous`), so a tap does
  // not blank the list it is narrowing.
  const onScreen: SearchResult | undefined =
    state.status === 'ready' ? state.result : state.status === 'loading' ? state.previous : undefined;
  if (state.status === 'idle' || (state.status === 'loading' && onScreen === undefined)) {
    return <LoadingState queryBn={q} />;
  }

  const actions = searchActions(controller, navigate);

  // A designed failure goes through the boundary that already maps the nine codes to
  // the eleven states, with the server's own `message_bn`. No mapping is written here,
  // and nothing is composed: an unmapped code renders the screen unchanged, which is
  // `states/boundary.tsx`'s documented answer to a condition nobody can name.
  if (state.status === 'designed_failure') {
    return (
      <SurfaceStates surface="discovery" error={state.error}>
        <SearchResults queryBn={q} filter={filter} actions={actions} />
      </SurfaceStates>
    );
  }

  // The answer never arrived. E_OFFLINE's frame is the one drawn for that, and retry is
  // the one way out it draws.
  if (state.status === 'unreachable' || onScreen === undefined) {
    return <OfflineState onRetry={controller.retry} />;
  }

  const result = onScreen;

  // The server said this is a conversation. The answer, the understood intent and the
  // correction control are all drawn in C2, so this route hands over rather than
  // rendering a second answer surface.
  if (result.route === 'ai' && result.conversation_id !== undefined) {
    return <Navigate to={`/ai/${result.conversation_id}`} replace />;
  }

  // …and a conversation lives in an account. Somebody who has not signed in is shown the
  // door, carrying the query, rather than an empty results screen: the search did not fail.
  if (result.route === 'ai' && result.sign_in !== undefined) {
    return (
      <SearchSignIn
        queryBn={q}
        copy={result.sign_in}
        href={searchSignInHref(q, isFirstRun())}
        actions={actions}
      />
    );
  }

  if (result.route === 'domain_not_live' && result.domain_not_live !== undefined) {
    return (
      <DomainNotLive
        queryBn={q}
        domainId={result.intent.domain_id}
        detail={result.domain_not_live}
        signInTo={searchSignInHref(q, isFirstRun())}
        actions={actions}
      />
    );
  }

  return (
    <SearchResults
      queryBn={q}
      result={result}
      filter={filter}
      busy={state.status === 'loading'}
      actions={actions}
    />
  );
}

/* ------------------------------------------------------------ B2 · খালি --- */

/** The field's id — what the pinned AI card is the label of. */
export const B2_INPUT_ID = 'b2-query';

/**
 * B2's two lists, from where each actually lives. Loaded once, after the first paint; an
 * empty list is a section that is not drawn.
 */
function useB2Lists(): { recents: readonly string[]; popular: readonly string[] } {
  const [recents, setRecents] = useState<readonly string[]>([]);
  const [popular, setPopular] = useState<readonly string[]>([]);
  useEffect(() => {
    let live = true;
    void loadRecents().then((rows) => {
      if (live) setRecents(rows);
    });
    void loadPopular().then((rows) => {
      if (live) setPopular(rows);
    });
    return () => {
      live = false;
    };
  }, []);
  return { recents, popular };
}

/**
 * `padding:16px` on the frame. The field is a 48px box with a `1.5px` teal rule and a
 * drawn caret — § 02's `Search` is 52px with a 1px rule, a `#8B9795` magnifier and two
 * trailing controls, none of which this frame has. Transcribed from B2 and the
 * difference reported.
 */
function SearchEmpty({ onSubmit }: { onSubmit: (q: string) => void }): JSX.Element {
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const { recents, popular } = useB2Lists();

  return (
    <SearchEmptyView
      value={value}
      onChange={setValue}
      onSubmit={onSubmit}
      onBack={() => navigate(-1)}
      recents={recents}
      popular={popular}
    />
  );
}

export interface SearchEmptyViewProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (q: string) => void;
  onBack: () => void;
  /** The person's own recent searches, newest first. Empty: the section is not drawn. */
  recents: readonly string[];
  /** The Worker's popular chips. Empty: the section is not drawn. */
  popular: readonly string[];
}

/** B2, hook-free. */
export function SearchEmptyView({
  value,
  onChange,
  onSubmit,
  onBack,
  recents,
  popular,
}: SearchEmptyViewProps): JSX.Element {
  return (
    <Frame className={`${SAND_FRAME} p-[16px]`}>
      <div className="flex gap-[12px] items-center mb-[20px]">
        <IconButton control="back" size={20} onClick={onBack} className="flex-none" />
        <form
          className="flex-1 min-w-0 border-[1.5px] border-teal bg-white box-content h-[48px] flex items-center px-[13px] gap-[10px]"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
          }}
        >
          {/* `width:1.5px;height:20px;background:#0E3A45` — the drawn caret. */}
          <div className="w-[1.5px] h-[20px] bg-teal flex-none" aria-hidden />
          <input
            id={B2_INPUT_ID}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={B2_COPY.placeholder_bn}
            aria-label={B2_COPY.placeholder_bn}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none rounded-none font-anek font-normal text-[16px] leading-[1.75] text-ink placeholder:text-muted"
          />
        </form>
      </div>

      {recents.length === 0 ? null : (
        <>
          <div className="font-anek font-semibold text-[14px] leading-[1.75] text-muted mb-[4px]">
            {B2_COPY.recents_heading_bn}
          </div>
          <div>
            {recents.map((query) => (
              <button
                key={query}
                type="button"
                onClick={() => onSubmit(query)}
                className="w-full py-[13px] border-b border-border flex gap-[12px] items-center bg-transparent px-0 rounded-none text-left"
              >
                <Glyph name="clock" size={16} stroke="#5B6B69" className="flex-none" />
                <span className="flex-1 font-anek font-normal text-[16px] leading-[1.75] text-ink2">{query}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {popular.length === 0 ? null : (
        <>
          <div
            className={`font-anek font-semibold text-[14px] leading-[1.75] text-muted mb-[11px] ${
              recents.length === 0 ? '' : 'mt-[22px]'
            }`}
          >
            {B2_COPY.popular_heading_bn}
          </div>
          {/*
            B2 draws its popular-intent chips `padding:9px 14px`; `Chip` carries § 03's
            `8px 13px`, which no P0 frame uses. The override is safe rather than lucky:
            Tailwind emits arbitrary values of one utility in ascending order, so
            `px-[14px]`/`py-[9px]` are always later in the sheet than `px-[13px]`/`py-[8px]`.
            The real fix belongs in `@oprappo/ui` — reported, not edited.
          */}
          <div className="flex flex-wrap gap-[8px]">
            {popular.map((label) => (
              <Chip
                key={label}
                labelBn={label}
                className="bg-white px-[14px] py-[9px]"
                onClick={() => onSubmit(label)}
              />
            ))}
          </div>
        </>
      )}

      <div className="flex-1" />

      {/*
        The one amber element on this screen: `border:1px solid #E8A33D`. Pinned so it is
        reachable without scrolling back.

        It is the field's LABEL. The card tells the person to write the whole sentence,
        and tapping it puts the cursor where the sentence goes — the platform's own
        behaviour for a label, with no script and nothing to fail. Its children are spans
        with the drawn divs' display, because a label holds phrasing content.
      */}
      <label
        htmlFor={B2_INPUT_ID}
        className="bg-white border border-amber p-[14px] flex gap-[12px] items-center cursor-text"
      >
        <Icon name="ai" size={22} stroke="#8A5A0C" className="flex-none" />
        <span className="block flex-1">
          <span className="block font-anek font-semibold text-[15px] leading-[1.75] text-ink">
            {B2_COPY.ai_card_title_bn}
          </span>
          <span className="block font-anek font-normal text-[14px] leading-[1.75] text-body mt-[1px]">
            {B2_COPY.ai_card_body_bn}
          </span>
        </span>
      </label>
    </Frame>
  );
}

/* --------------------------------------------------------- B3 · ফলাফল --- */

/**
 * One result card.
 *
 * ── ONE CARD, NOT ONE PER OFFERING TYPE ─────────────────────────────────────
 * B3 draws two cards that look different: a professional with a যাচাইকৃত badge and a
 * rating/jobs/ETA row under a rule, and a priced package with the amount top-right. They
 * are the SAME card with different fields filled in — a badge or an amount on the right,
 * a stat row or nothing below — so this renders the fields the server sent and omits the
 * ones it did not. `offering_type` has eleven values (0003_offerings.sql) and a component
 * per type would be eleven transcriptions of one frame, ten of which nobody drew.
 *
 * NOTHING IS DEFAULTED. An absent `verified_label_bn` means the provider is not verified,
 * and drawing the badge anyway is the single most expensive lie this screen could tell —
 * the badge is a claim Oprappo makes about somebody else's work (audit finding 04). An
 * absent price is no price, never `৳ 0`; `activity.ts § ActivityCostLine` makes the same
 * argument in the shape of a type.
 *
 * ── IT OPENS WHAT IT DESCRIBES ──────────────────────────────────────────────
 * With a `to`, the card is a button that opens `/offering/<slug>` — the path the door
 * composed, never one assembled here. Without one the listing has no page, and the card
 * is the same box as a plain block: it renders, and it does not pretend to open. Inside
 * the button every drawn `div` is a `span` with the same display, because a button holds
 * phrasing content; the geometry is the frame's either way.
 */
export function ResultCard({ item, onOpen }: { item: SearchResultItem; onOpen: (to: string) => void }): JSX.Element {
  const stats = [item.rating_bn, item.jobs_bn, item.eta_bn];
  const hasStats = stats.some((v) => v !== undefined && v !== '');
  const to = item.to !== undefined && item.to !== '' ? item.to : undefined;

  const body = (
    <>
      <span className="flex justify-between items-start gap-[10px]">
        <span className="block flex-1">
          {item.title_bn === undefined || item.title_bn === '' ? null : (
            <span className="block font-anek font-semibold text-[16px] leading-[1.75] text-ink">{item.title_bn}</span>
          )}
          {item.meta_bn === undefined || item.meta_bn === '' ? null : (
            <span className="block font-anek font-normal text-[14px] leading-[1.75] text-muted mt-[1px]">
              {item.meta_bn}
            </span>
          )}
        </span>
        {item.verified_label_bn !== undefined && item.verified_label_bn !== null && item.verified_label_bn !== '' ? (
          // Drawn as its own square badge: `border:1px solid #1E6B4A; padding:3px 8px`.
          // Not `VerifiedBadge`, which § 01 draws with a tick — see the report.
          <span className="block border border-success px-[8px] py-[3px] font-anek font-medium text-[13px] leading-[1.75] text-success flex-none">
            {item.verified_label_bn}
          </span>
        ) : null}
        {item.amount_minor === undefined ? null : (
          // Latin numerals: Manrope with tabular figures, exactly as drawn.
          <span className="block font-manrope font-semibold text-[16px] leading-[1] text-ink [font-variant-numeric:tabular-nums] flex-none">
            {formatTaka(item.amount_minor)}
          </span>
        )}
      </span>
      {hasStats ? (
        <span className="flex gap-[15px] mt-[11px] pt-[11px] border-t border-border font-anek font-medium text-[14px] leading-[1.75]">
          {item.rating_bn === undefined ? null : <span className="block text-ink">{item.rating_bn}</span>}
          {item.jobs_bn === undefined ? null : <span className="block text-muted">{item.jobs_bn}</span>}
          {item.eta_bn === undefined ? null : <span className="block text-muted">{item.eta_bn}</span>}
        </span>
      ) : null}
    </>
  );

  return to === undefined ? (
    <div className="block bg-white border border-border p-[13px]">{body}</div>
  ) : (
    <button
      type="button"
      data-to={to}
      onClick={() => onOpen(to)}
      className="block w-full bg-white border border-border p-[13px] text-left rounded-none"
    >
      {body}
    </button>
  );
}

/**
 * A white row with a chevron — the eleventh state's alternative row, as drawn — used for
 * every "go there instead" the Worker names: a browse row, an alternative.
 */
function RowButton({
  label,
  sub,
  to,
  onOpen,
}: {
  label: string;
  sub?: string | undefined;
  to: string;
  onOpen: (to: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-to={to}
      onClick={() => onOpen(to)}
      className="bg-white border border-border p-[13px] flex justify-between items-center gap-[11px] text-left rounded-none w-full"
    >
      <span className="flex-1">
        <span className="block font-anek font-medium text-[15px] leading-[1.75] text-ink">{label}</span>
        {sub === undefined || sub === '' ? null : (
          <span className="block font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[1px]">{sub}</span>
        )}
      </span>
      <Glyph name="rowChevron" size={15} stroke="#5B6B69" className="flex-none" />
    </button>
  );
}

/**
 * What the Worker said about the chip, when it said anything: a filter that matched
 * nothing, or `কাছাকাছি` with no area to measure from — and then where the area is
 * entered. Every word is the Worker's, and AUTHORED (`data-copy-status`).
 */
function FilterNote({ state, onOpen }: { state: SearchFilterState; onOpen: (to: string) => void }): JSX.Element | null {
  if (state.message_bn === undefined || state.message_bn === '') return null;
  const action = state.action_bn !== undefined && state.action_bn !== '' && state.to !== undefined ? state.to : undefined;
  return (
    <div data-copy-status={state.copy_status ?? 'authored'} className="bg-white border border-border p-[15px]">
      <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">{state.message_bn}</div>
      {action === undefined ? null : (
        <button
          type="button"
          data-to={action}
          onClick={() => onOpen(action)}
          className="mt-[12px] border-[1.5px] border-teal px-[16px] py-[10px] font-anek font-semibold text-[14.5px] leading-[1.75] text-teal bg-transparent rounded-none"
        >
          {state.action_bn}
        </button>
      )}
    </div>
  );
}

/** The answer that always exists: one authored sentence, and the rows it points at. */
function BrowseList({ browse, onOpen }: { browse: SearchBrowse; onOpen: (to: string) => void }): JSX.Element {
  return (
    <div data-copy-status={browse.copy_status}>
      <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mb-[12px]">{browse.sentence_bn}</div>
      <div className="flex flex-col gap-[10px]">
        {browse.links.map((link) => (
          <RowButton key={link.to} label={link.label_bn} to={link.to} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

export interface SearchResultsProps {
  queryBn: string;
  /** The door's answer. Absent: the chrome alone (a designed failure is drawn over it). */
  result?: SearchResult | undefined;
  /** The chip that is lit. */
  filter: SearchFilter;
  /** A chip's question is in flight and `result` is the answer before it. */
  busy?: boolean | undefined;
  actions: SearchActions;
}

/**
 * B3 · সার্চ ফলাফল, rendered from the door's answer.
 *
 * ── WHAT THIS USED TO DO ────────────────────────────────────────────────────
 * It rendered two constants — `রহিম ইলেকট্রিক` and `শাহীন কুলিং সার্ভিস` — and one priced
 * package, under two headings with two counts, for EVERY query. A search for a plumber
 * returned an air-conditioning technician with a rating nobody had earned and an ETA
 * nobody had promised. The screen was drawn correctly and was wrong about everything on it.
 *
 * Now the groups, their headings, their counts and their cards are `result.groups`, and
 * the screen renders exactly what the door sent. It still branches on nothing about the
 * query text: `SearchScreen` above switches on `result.route`, which is the server's own
 * classification (audit finding 01, `./client.ts`).
 *
 * ── AN EMPTY STRING RENDERS NOTHING, AND SO DOES AN ABSENT COUNT ────────────
 * `label_bn` and `count_bn` arrive from the door, and for nine of the eleven offering
 * types they arrive EMPTY. The reason is upstream, not here: B3 draws two headings and
 * generalising them to eleven types would be inventing copy by extrapolation —
 * `professional` covers a lawyer and an accountant as well as a technician
 * (`apps/api/src/search/copy.ts § DRAWN_GROUPS`, and `GROUP_LABEL_COPY_NEEDS`, which the
 * response reports through `copy_status`). An empty heading is not drawn; the group's
 * cards are, so they render under no heading at all.
 *
 * The count stays a string the server sends whole, for the reason in
 * `./client.ts § SearchResultGroup`: `১২ জন` and `৩টি` use different counters, and
 * choosing between them is grammar. This screen never assembles one from `count`.
 *
 * ── AND WHEN THERE ARE NO GROUPS AT ALL ─────────────────────────────────────
 * The chrome renders — the back arrow, the query still in its field, the four chips — and
 * under it whatever the Worker said instead: the chip's own sentence when a filter
 * explains the emptiness, or the browse rows (the matched domain's categories, or the live
 * domains) with their one sentence. Both are the Worker's, both AUTHORED and marked so.
 * When it said neither, the column is empty, which says nothing untrue.
 */
export function SearchResults({ queryBn, result, filter, busy = false, actions }: SearchResultsProps): JSX.Element {
  const groups = result?.groups ?? [];
  const note = result?.filter;
  const browse = groups.length === 0 ? result?.browse : undefined;

  return (
    <Frame className={SAND_FRAME}>
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-[16px] pt-[16px]">
          <div className="flex gap-[12px] items-center mb-[14px]">
            <IconButton control="back" size={20} onClick={actions.back} className="flex-none" />
            {/* `box-content` — `height:46px` + `border:1px`, drawn as 48px outer. */}
            <div className="flex-1 min-w-0 box-content border border-border bg-white h-[46px] flex items-center px-[13px] font-anek font-medium text-[16px] leading-[1.75] text-ink truncate">
              {queryBn}
            </div>
          </div>
          <div className="flex gap-[8px] overflow-x-auto pb-[14px]">
            {RESULT_FILTERS.map((f) => (
              <Chip
                key={f.id}
                labelBn={f.label_bn}
                selected={f.id === filter}
                onClick={() => actions.filter(f.id)}
                // B3 draws its filter chips `padding:8px 14px`; `Chip` carries § 03's
                // `8px 13px`. Reported — the horizontal value is corrected here.
                className={f.id === filter ? 'flex-none px-[14px]' : 'flex-none bg-white px-[14px]'}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 px-[16px] pb-[16px] overflow-y-auto" aria-busy={busy}>
          {groups.length === 0 && note !== undefined ? <FilterNote state={note} onOpen={actions.open} /> : null}
          {browse === undefined ? null : <BrowseList browse={browse} onOpen={actions.open} />}
          {groups.map((g, gi) => (
            <div key={`${g.type}-${String(gi)}`} className={gi === groups.length - 1 ? '' : 'mb-[20px]'}>
              {g.label_bn === '' ? null : (
                <div className="flex justify-between items-baseline mb-[10px]">
                  <div className="font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                    {g.label_bn}
                    {g.count_bn === undefined || g.count_bn === null || g.count_bn === '' ? null : (
                      <>
                        {' '}
                        <span className="text-muted font-normal">{g.count_bn}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-[10px]">
                {g.items.map((item, i) => (
                  <ResultCard key={`${item.offering_id ?? item.id ?? ''}-${String(i)}`} item={item} onOpen={actions.open} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

/* ---------------------------------------- a conversation, signed out --- */

/**
 * The Worker read the sentence as a conversation, and nobody is signed in to have one.
 *
 * Not a failure and not a state: the answer exists and an account is what stands between
 * the person and it. The heading, body and button are the Worker's (`sign_in`, AUTHORED —
 * `@oprappo/contracts § SEARCH_SIGN_IN_BN`); the door is a real link with the query in
 * `next`, so the same search is asked again, signed in, when they come back — and then it
 * opens the conversation. The geometry is the eleventh state's card on B3's chrome: no
 * frame draws this moment, and those are the two nearest drawn shapes.
 */
export function SearchSignIn({
  queryBn,
  copy,
  href,
  actions,
}: {
  queryBn: string;
  copy: SearchSignInCopy;
  href: string;
  actions: SearchActions;
}): JSX.Element {
  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[16px]">
          <IconButton control="back" size={20} onClick={actions.back} className="flex-none" />
          <div className="flex-1 min-w-0 box-content border border-border bg-white h-[46px] flex items-center px-[13px] font-anek font-medium text-[16px] leading-[1.75] text-ink truncate">
            {queryBn}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 px-[16px] overflow-y-auto">
        <div
          data-copy-status={copy.copy_status}
          className="bg-white border border-border border-l-[3px] border-l-amber-text p-[15px]"
        >
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{copy.title_bn}</div>
          <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">{copy.body_bn}</div>
          <div className="flex gap-[9px] mt-[14px]">
            <a
              href={href}
              onClick={(event) => {
                event.preventDefault();
                actions.open(href);
              }}
              className="bg-teal px-[16px] py-[12px] font-anek font-semibold text-[14.5px] leading-[1.75] text-white no-underline"
            >
              {copy.action_bn}
            </a>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ------------------------------------------- ১১ · সেবাটি আসছে (state) --- */

/**
 * The eleventh state's rows — "things that DO work" — each opening the listing the Worker
 * found for it. The Worker sends a row only beside that listing (`apps/api/src/search/
 * index.ts § the alternatives`), so every row here leads somewhere real.
 */
export function NotLiveAlternatives({
  alternatives,
  onOpen,
}: {
  alternatives: readonly DomainNotLiveAlternative[];
  onOpen: (to: string) => void;
}): JSX.Element | null {
  if (alternatives.length === 0) return null;
  return (
    <>
      <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mt-[20px] mb-[11px]">
        {DOMAIN_NOT_LIVE_COPY.alternatives_heading_bn}
      </div>
      <div className="flex flex-col gap-[10px]">
        {alternatives.map((a) => (
          <RowButton key={a.id} label={a.label_bn} sub={a.where_bn} to={a.to} onOpen={onOpen} />
        ))}
      </div>
    </>
  );
}

/**
 * The eleventh state. Its job is to be useful, not apologetic: two things that do work,
 * a notify action, and the search already logged as demand (docs/05 — the Worker writes
 * the `demand_signals` row before responding, so by the time this renders it is done).
 *
 * Every string is server-supplied; nothing here is composed from fragments.
 *
 * AN EMPTY STRING RENDERS NOTHING. The server sends `''` for a sentence that does not
 * exist — the state is drawn for one of the seven unlaunched domains, the demand line is
 * withheld when the row was not actually written, and the coverage footer when its count
 * is no longer true — so each block below is gated on its own string. A blank card with
 * drawn padding would read as a rendering bug; the absence reads as what it is.
 *
 * `চালু হলে জানান` RECORDS SOMETHING NOW. It posts the request (`POST /v1/search/notify`),
 * and the button gives way to the Worker's own sentence saying how the person will be told
 * — in-app, the one channel it promises. Somebody who has not signed in is sent through the
 * sign-in door with the query, because a notification needs an account to arrive in.
 */
function DomainNotLive({
  queryBn,
  domainId,
  detail,
  signInTo,
  actions,
}: {
  queryBn: string;
  domainId: number;
  detail: DomainNotLiveDetail;
  signInTo: string;
  actions: SearchActions;
}): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const notify = (): void => {
    if (sending) return;
    setSending(true);
    postNotify(domainId)
      .then((answer) => setNotice(answer.message_bn))
      .catch((err: unknown) => {
        if (isSignedOut(err)) {
          actions.open(signInTo);
          return;
        }
        // Nothing was recorded, so nothing is claimed: the button stays.
        console.error('POST /v1/search/notify failed', err);
      })
      .finally(() => setSending(false));
  };

  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[16px]">
          <IconButton control="back" size={20} onClick={actions.back} className="flex-none" />
          <div className="flex-1 min-w-0 border border-border bg-white h-[46px] flex items-center px-[13px] font-anek font-medium text-[16px] leading-[1.75] text-ink truncate">
            {queryBn}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-[16px] overflow-y-auto">
        {/* `border-left:3px solid #8A5A0C` — amber-text, not amber, as drawn. */}
        <div className="bg-white border border-border border-l-[3px] border-l-amber-text p-[15px]">
          {detail.title_bn === '' ? null : (
            <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{detail.title_bn}</div>
          )}
          {detail.body_bn === '' ? null : (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">
              {detail.body_bn}
            </div>
          )}
          {!detail.notify_available ? null : notice === null ? (
            <div className="flex gap-[9px] mt-[14px]">
              <button
                type="button"
                onClick={notify}
                disabled={sending}
                className="bg-teal px-[16px] py-[12px] font-anek font-semibold text-[14.5px] leading-[1.75] text-white border-0 rounded-none"
              >
                {DOMAIN_NOT_LIVE_COPY.notify_action_bn}
              </button>
            </div>
          ) : (
            <div
              role="status"
              data-copy-status="authored"
              className="font-anek font-medium text-[14.5px] leading-[1.75] text-ink mt-[14px]"
            >
              {notice}
            </div>
          )}
        </div>

        <NotLiveAlternatives alternatives={detail.alternatives} onOpen={actions.open} />

        {/*
          The demand line is a CLAIM about a database row, and the server withholds it
          when the write did not happen. Rendering it from an empty string would put the
          claim back on the screen with nothing behind it.
        */}
        {detail.demand_note_bn === '' ? null : (
          <div className="border-t border-border mt-[18px] pt-[14px]">
            <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-body">{detail.demand_note_bn}</div>
          </div>
        )}
      </div>

      {/* The count of live domains, sent only while it is true (`copy.ts § coverageBn`). */}
      {detail.coverage_bn === '' ? null : (
        <div className="bg-sand border-t border-border px-[16px] py-[12px]">
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">{detail.coverage_bn}</div>
        </div>
      )}
    </Frame>
  );
}
